import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react'
import { Platform, Pressable, ScrollView, Text, View } from 'react-native'
import styled from 'styled-components/native'
import { Feather } from '../../../components/icon/vectorIcons'
import { useQuery } from '@apollo/client'
import { ScreenContainerWithItemHeader } from '../../../components/layout/ScreenContainer'
import Button from '../../../components/common/Button'
import useTranslation from '../../../hooks/useTranslation'
import usePreventNavOnDirtyState from '../../../hooks/usePreventNavigationOnDirtyState'
import DocletPage from '../../../doclets/DocletPage'
import { BlockType, DocletHostMedia } from '../../../doclets/types'
import {
  createPresetProps,
  TextPreset
} from '../../../doclets/blocks/text/presets'
import { firstTitleBlockTitle } from '../../../doclets/displayName'
import { getShareDocletQuery, ShareDocletData } from '../api'
import { builderReducer, createBuilderState } from './builderReducer'
import BlockSidebar from './BlockSidebar'
import BlockFrame from './BlockFrame'
import DocumentSettingsPanel from './DocumentSettingsPanel'
import PublishSharingPanel from './PublishSharingPanel'
import { DraftConflictModal, SaveStatusIndicator } from './BuilderNotices'
import useDocletAutosave from './useDocletAutosave'
import useRemoteDocletSaves from './useRemoteDocletSaves'
import openDocletPreview from './openDocletPreview'
import DeleteDocletDialog, { DocletDeletedModal } from '../DeleteDocletDialog'
import useLinkToScreen from '../../../hooks/useLinkToScreen'
import useToast, { ToastType } from '../../../hooks/useToast'
import { RecordHostMediaContext, toDocletHostMedia } from './hostMediaRecords'
import TextBlockEditor from './blocks/TextBlockEditor'
import ImageBlockEditor from './blocks/ImageBlockEditor'
import ImageTextBlockEditor from './blocks/ImageTextBlockEditor'
import { CLOUDINARY_NAME } from '../cloudinary'
import MediaBlockEditor from './blocks/MediaBlockEditor'
import { builderColors, builderRadii, builderShadows } from './builderColors'

/**
 * Loads a doclet and hands it to the builder. Reloading (after a conflict)
 * refetches and remounts the builder so it starts from the fresh draft.
 */
const DocletBuilderScreen = ({ route }) => {
  const { t } = useTranslation()
  const id: string = route?.params?.id
  const [loadKey, setLoadKey] = useState(0)
  const { data, loading, error, refetch } = useQuery(getShareDocletQuery, {
    variables: { id },
    fetchPolicy: 'network-only',
    skip: !id
  })

  const reload = useCallback(async () => {
    await refetch()
    setLoadKey(key => key + 1)
  }, [refetch])

  const doclet: ShareDocletData | undefined = data?.shareDoclet

  return (
    <ScreenContainerWithItemHeader
      screenCategory="doclets"
      title={doclet?.displayName || t('doclets:untitled')}
      defaultBack="DocletList"
    >
      {doclet ? (
        <DocletBuilder key={loadKey} doclet={doclet} onReload={reload} />
      ) : (
        <Message>
          {loading
            ? t('common:loading')
            : error
            ? t('doclets:builder:loadFailed')
            : null}
        </Message>
      )}
    </ScreenContainerWithItemHeader>
  )
}

interface DocletBuilderProps {
  doclet: ShareDocletData
  onReload: () => void
}

/**
 * The doclet builder: a sidebar of block types on the left and the doclet page
 * on the right, edited inline and autosaved.
 */
const DocletBuilder = ({ doclet, onReload }: DocletBuilderProps) => {
  const { t } = useTranslation()
  // Loaded once per mount; the builder owns the content from here on
  const [loaded] = useState(() => ({
    content: doclet.draftContent,
    draftVersion: doclet.draftVersion
  }))
  const [state, dispatch] = useReducer(
    builderReducer,
    loaded.content,
    createBuilderState
  )
  const { content, selectedBlockId } = state
  const canvasRef = useRef<ScrollView>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [sharingOpen, setSharingOpen] = useState(false)
  // Host media records the Media editors have reported since loading (newer
  // than, or missing from, doclet.hostMedia)
  const [reportedHostMedia, setReportedHostMedia] = useState<
    Record<string, DocletHostMedia>
  >({})
  const recordHostMedia = useCallback(
    (media: Parameters<typeof toDocletHostMedia>[0]) => {
      const record = toDocletHostMedia(media)
      if (record) {
        setReportedHostMedia(current => ({ ...current, [record.id]: record }))
      }
    },
    []
  )
  const hostMedia = useMemo(
    () =>
      Object.values({
        ...Object.fromEntries(
          (doclet.hostMedia ?? []).map(record => [record.id, record])
        ),
        ...reportedHostMedia
      }),
    [doclet.hostMedia, reportedHostMedia]
  )

  const autosave = useDocletAutosave({
    docletId: doclet.id,
    content,
    loadedContent: loaded.content,
    loadedDraftVersion: loaded.draftVersion
  })
  const checkForRemoteSaves = useRemoteDocletSaves(
    doclet.id,
    autosave.reportRemoteVersion
  )
  // Deleted from Document settings: leave for the list. The unsaved-changes
  // guard is off by then, so it doesn't ask about a doclet that's gone.
  const linkToScreen = useLinkToScreen()
  const { showToast } = useToast()
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deletedHere, setDeletedHere] = useState(false)
  // Nothing is left to save once the doclet is deleted, here or elsewhere
  usePreventNavOnDirtyState({
    dirty:
      autosave.hasUnsavedChanges &&
      autosave.status !== 'deleted' &&
      !deletedHere
  })
  useEffect(() => {
    if (deletedHere) linkToScreen('DocletList')
  }, [deletedHere, linkToScreen])

  const addBlock = useCallback((blockType: BlockType, preset?: TextPreset) => {
    dispatch({
      type: 'add',
      blockType,
      props: preset ? createPresetProps(preset) : undefined
    })
    // New blocks go at the bottom of the page; bring them into view
    setTimeout(() => canvasRef.current?.scrollToEnd({ animated: true }), 0)
  }, [])

  const deselect = useCallback(() => dispatch({ type: 'select', id: null }), [])
  // Pressing on the canvas outside every block deselects. On mouse down, not
  // click: a click goes to the element holding both ends of a drag, so
  // dragging a text selection out of a block would otherwise land on the
  // canvas and deselect. Presses inside dialogs that blocks open (rendered
  // elsewhere in the page, but bubbling through the canvas in React) are
  // ignored.
  const deselectOnCanvasPress = useCallback(
    (event: { target: EventTarget; currentTarget: HTMLElement }) => {
      const target = event.target as HTMLElement
      if (!event.currentTarget.contains(target)) return
      if (target.closest('[data-doclet-block]')) return
      deselect()
    },
    [deselect]
  )

  useEffect(() => {
    if (Platform.OS !== 'web') return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') deselect()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [deselect])

  const lastIndex = content.blocks.length - 1
  // Live: follows the Title block as it's typed, unless a name is set
  const titleFromPage = firstTitleBlockTitle(content)
  const displayName = doclet.name || titleFromPage
  const selectedBlock =
    content.blocks.find(block => block.id === selectedBlockId) ?? null

  return (
    <View style={{ flex: 1 }}>
      <Toolbar onPress={deselect}>
        <DocletName numberOfLines={1}>
          {displayName || t('doclets:untitled')}
        </DocletName>
        <SaveStatusIndicator status={autosave.status} />
        <StatusPill>
          <StatusPillText>
            {doclet.isPublished
              ? doclet.hasUnpublishedChanges
                ? t('doclets:sharing:pill:unpublishedChanges')
                : t('doclets:sharing:pill:published')
              : t('doclets:sharing:pill:draft')}
          </StatusPillText>
        </StatusPill>
        <Button
          label={t('doclets:builder:preview')}
          icon={
            <Feather
              name="external-link"
              size={16}
              color={builderColors.text}
              style={{ marginRight: 8 }}
            />
          }
          onPress={() => openDocletPreview(doclet.id)}
          buttonStyle={{
            backgroundColor: '#FFFFFF',
            borderWidth: 1,
            borderColor: builderColors.border
          }}
          titleStyle={{ color: builderColors.text }}
        />
        <Button
          label={t('doclets:builder:documentSettings')}
          icon={
            <Feather
              name="settings"
              size={16}
              color={builderColors.text}
              style={{ marginRight: 8 }}
            />
          }
          onPress={() => setSettingsOpen(true)}
          style={{ marginLeft: 8 }}
          buttonStyle={{
            backgroundColor: '#FFFFFF',
            borderWidth: 1,
            borderColor: builderColors.border
          }}
          titleStyle={{ color: builderColors.text }}
        />
        <Button
          label={t('doclets:sharing:title')}
          icon={
            <Feather
              name="share-2"
              size={16}
              color="#FFFFFF"
              style={{ marginRight: 8 }}
            />
          }
          onPress={() => setSharingOpen(true)}
          style={{ marginLeft: 8 }}
        />
      </Toolbar>
      <View style={{ flex: 1, flexDirection: 'row' }}>
        <BlockSidebar
          onAdd={addBlock}
          selectedBlock={selectedBlock}
          onToggleHighlight={id => dispatch({ type: 'toggleHighlight', id })}
          onToggleFullWidth={id => dispatch({ type: 'toggleFullWidth', id })}
          onChangeBlock={(id, props) => dispatch({ type: 'update', id, props })}
          onDeselect={deselect}
        />
        <ScrollView
          ref={canvasRef}
          style={{ flex: 1, backgroundColor: builderColors.canvas }}
          contentContainerStyle={{ flexGrow: 1 }}
        >
          <div
            onMouseDown={deselectOnCanvasPress}
            style={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              padding: 24
            }}
            data-testid="doclet-canvas"
          >
            <RecordHostMediaContext.Provider value={recordHostMedia}>
              <DocletPage
                content={content}
                editable
                onChangeBlock={(id, props) =>
                  dispatch({ type: 'update', id, props })
                }
                onFocusBlock={id => dispatch({ type: 'select', id })}
                selectedBlockId={selectedBlockId}
                editComponents={EDIT_COMPONENTS}
                cloudinaryCloudName={CLOUDINARY_NAME}
                hostMedia={hostMedia}
                emptyPlaceholder={
                  <Text
                    style={{
                      color: builderColors.mutedText,
                      textAlign: 'center',
                      paddingVertical: 80
                    }}
                  >
                    {t('doclets:builder:emptyPage')}
                  </Text>
                }
                pageStyle={{
                  borderRadius: builderRadii.md,
                  ...builderShadows.md,
                  minHeight: 240
                }}
                renderBlockFrame={(block, index, rendered) => (
                  <BlockFrame
                    label={t(`doclets:blocks:${block.type}:label`)}
                    selected={block.id === selectedBlockId}
                    isFirst={index === 0}
                    isLast={index === lastIndex}
                    onSelect={() => dispatch({ type: 'select', id: block.id })}
                    onMoveUp={() => dispatch({ type: 'moveUp', id: block.id })}
                    onMoveDown={() =>
                      dispatch({ type: 'moveDown', id: block.id })
                    }
                    onDuplicate={() =>
                      dispatch({ type: 'duplicate', id: block.id })
                    }
                    onRemove={() => dispatch({ type: 'remove', id: block.id })}
                  >
                    {rendered}
                  </BlockFrame>
                )}
              />
            </RecordHostMediaContext.Provider>
          </div>
        </ScrollView>
      </View>
      <DocumentSettingsPanel
        doclet={doclet}
        titleFromPage={titleFromPage}
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onDelete={() => {
          setSettingsOpen(false)
          setDeleteOpen(true)
        }}
      />
      <DeleteDocletDialog
        doclet={deleteOpen && !deletedHere ? doclet : null}
        onCancel={() => setDeleteOpen(false)}
        onDeleted={alreadyDeleted => {
          showToast(
            t(
              alreadyDeleted
                ? 'doclets:delete:alreadyDeleted'
                : 'doclets:delete:deleted'
            ),
            ToastType.INFO
          )
          setDeletedHere(true)
        }}
      />
      <DocletDeletedModal
        open={autosave.status === 'deleted' && !deletedHere}
        onBack={() => linkToScreen('DocletList')}
      />
      <PublishSharingPanel
        doclet={doclet}
        open={sharingOpen}
        onClose={() => setSharingOpen(false)}
        saveStatus={autosave.status}
        getDraftVersion={autosave.getDraftVersion}
      />
      <DraftConflictModal
        conflict={autosave.status === 'conflict' ? autosave.conflict : null}
        onNeedName={checkForRemoteSaves}
        onReload={onReload}
        onOverwrite={autosave.overwrite}
      />
    </View>
  )
}

// Block types whose editor needs MI6 components (so can't live in src/doclets)
const EDIT_COMPONENTS = {
  text: TextBlockEditor,
  image: ImageBlockEditor,
  imageText: ImageTextBlockEditor,
  media: MediaBlockEditor
}

// A Pressable so clicks on empty toolbar space deselect (its buttons handle
// their own presses)
const Toolbar = ({ children, onPress }) => (
  <Pressable
    onPress={onPress}
    // @ts-ignore web-only: empty toolbar space isn't a focus target
    focusable={false}
    style={{
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 10,
      paddingHorizontal: 16,
      backgroundColor: '#FFFFFF',
      borderBottomWidth: 1,
      borderBottomColor: builderColors.border
    }}
  >
    {children}
  </Pressable>
)

const StatusPill = styled(View)`
  margin: 0 12px 0 8px;
  padding: 2px 10px;
  border-width: 1px;
  border-color: ${builderColors.border};
  border-radius: 10px;
`

const StatusPillText = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  color: ${builderColors.mutedText};
`

const DocletName = styled(Text)`
  flex: 1;
  font-size: 15px;
  font-weight: 700;
  color: ${builderColors.text};
  margin-right: 16px;
`

const Message = styled(Text)`
  padding: 32px;
  color: ${builderColors.mutedText};
`

export default DocletBuilderScreen
